import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  exportTestRunCsv,
  listNoticeTemplates,
  listTemplateVersions,
  listTestRuns,
  previewNoticeTemplate,
  previewVariablesForRecipient,
  previewVariablesForRecipients,
  resetNoticeTemplate,
  restoreTemplateVersion,
  saveNoticeTemplate,
  sendTestNotice,
} from "@/lib/email-admin.functions";
import { alignedDiff, diffLines, diffSummary } from "@/lib/text-diff";
import {
  PLACEHOLDER_DOCS,
  analyzeTemplateVariables,
  validateTemplateText,
} from "@/lib/template-placeholders";

export const Route = createFileRoute("/_authenticated/admin/email-templates")({
  head: () => ({
    meta: [
      { title: "Email templates — Quibblies staff" },
      {
        name: "description",
        content:
          "Edit and preview the wording Quibblies sends for approvals, rejections, appeals and support replies.",
      },
      { property: "og:title", content: "Email templates — Quibblies staff" },
      {
        property: "og:description",
        content: "Change decision notice wording without a redeploy.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: EmailTemplatesPage,
});

const PLACEHOLDERS = PLACEHOLDER_DOCS.map((doc) => doc.token);

/**
 * Everything after the last unclosed "{{" before the caret. Returns null when
 * the caret is not inside a placeholder being typed.
 */
function autocompleteQuery(value: string, caret: number): string | null {
  const before = value.slice(0, caret);
  const open = before.lastIndexOf("{{");
  if (open === -1) return null;
  const fragment = before.slice(open + 2);
  if (fragment.includes("}") || fragment.includes("{") || /\s/.test(fragment)) return null;
  return fragment;
}

/** Replaces the half-typed variable at the caret with a complete one. */
function completeAt(value: string, caret: number, name: string) {
  const before = value.slice(0, caret);
  const open = before.lastIndexOf("{{");
  const after = value.slice(caret);
  const trimmedAfter = after.startsWith("}}") ? after.slice(2) : after;
  const next = `${value.slice(0, open)}{{${name}}}${trimmedAfter}`;
  return { next, caret: open + name.length + 4 };
}

function EmailTemplatesPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(listNoticeTemplates);
  const save = useServerFn(saveNoticeTemplate);
  const reset = useServerFn(resetNoticeTemplate);
  const preview = useServerFn(previewNoticeTemplate);
  const testSend = useServerFn(sendTestNotice);
  const loadVersions = useServerFn(listTemplateVersions);
  const restore = useServerFn(restoreTemplateVersion);

  const { data, isLoading, error } = useQuery({
    queryKey: ["notice-templates"],
    queryFn: () => load(),
    retry: false,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [rendered, setRendered] = useState<{ subject: string; body: string } | null>(null);
  const [testTo, setTestTo] = useState("");
  const [testLabel, setTestLabel] = useState("");
  const [compareId, setCompareId] = useState<string | null>(null);
  const [compareAgainst, setCompareAgainst] = useState<string>("current");
  const [sideBySide, setSideBySide] = useState(true);
  const [previewRecipient, setPreviewRecipient] = useState("");
  const [multiRecipients, setMultiRecipients] = useState("");
  const [versionNote, setVersionNote] = useState("");

  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [autocomplete, setAutocomplete] = useState<{
    field: "subject" | "body";
    query: string;
    caret: number;
    index: number;
  } | null>(null);

  const suggestions = autocomplete
    ? PLACEHOLDER_DOCS.filter((doc) =>
        doc.name.toLowerCase().startsWith(autocomplete.query.toLowerCase()),
      )
    : [];

  function fieldValue(field: "subject" | "body") {
    return field === "subject" ? subject : body;
  }

  function setFieldValue(field: "subject" | "body", value: string) {
    if (field === "subject") setSubject(value);
    else setBody(value);
  }

  /** Completes the half-typed variable, or inserts one at the caret. */
  function applyVariable(field: "subject" | "body", name: string) {
    const element = field === "subject" ? subjectRef.current : bodyRef.current;
    const value = fieldValue(field);
    const caret = element?.selectionStart ?? value.length;
    const typing = autocompleteQuery(value, caret) !== null;
    const result = typing
      ? completeAt(value, caret, name)
      : {
          next: `${value.slice(0, caret)}{{${name}}}${value.slice(caret)}`,
          caret: caret + name.length + 4,
        };
    setFieldValue(field, result.next);
    setAutocomplete(null);
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(result.caret, result.caret);
    });
  }

  function trackCaret(field: "subject" | "body", element: HTMLInputElement | HTMLTextAreaElement) {
    const caret = element.selectionStart ?? element.value.length;
    const query = autocompleteQuery(element.value, caret);
    setAutocomplete(query === null ? null : { field, query, caret, index: 0 });
  }

  function handleKeyDown(
    field: "subject" | "body",
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    if (!autocomplete || autocomplete.field !== field || suggestions.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      setAutocomplete({
        ...autocomplete,
        index: (autocomplete.index + delta + suggestions.length) % suggestions.length,
      });
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      const choice = suggestions[autocomplete.index] ?? suggestions[0];
      if (choice) applyVariable(field, choice.name);
    } else if (event.key === "Escape") {
      setAutocomplete(null);
    }
  }

  const problems = validateTemplateText(subject, body);
  const exportRun = useServerFn(exportTestRunCsv);
  const runCsvMutation = useMutation({
    mutationFn: (runId: string) => exportRun({ data: { runId } }),
    onSuccess: (result) => {
      const url = URL.createObjectURL(new Blob([result.csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Test run CSV downloaded");
    },
    onError: (e: Error) => toast.error("Could not export that run", { description: e.message }),
  });
  const loadTestRuns = useServerFn(listTestRuns);
  const { data: testRuns } = useQuery({
    queryKey: ["notice-test-runs", selected],
    queryFn: () => loadTestRuns({ data: { template: selected! } }),
    enabled: Boolean(selected),
    retry: false,
  });

  const { data: versions } = useQuery({
    queryKey: ["notice-template-versions", selected],
    queryFn: () => loadVersions({ data: { template: selected! } }),
    enabled: Boolean(selected),
    retry: false,
  });

  const current = data?.find((row) => row.template === selected) ?? null;

  useEffect(() => {
    if (!data?.length) return;
    const next = data.find((row) => row.template === selected) ?? data[0];
    if (!next) return;
    if (next.template !== selected) {
      setSelected(next.template);
      setSubject(next.subject);
      setBody(next.body);
      setRendered(null);
    }
  }, [data, selected]);

  function choose(template: string) {
    const row = data?.find((item) => item.template === template);
    if (!row) return;
    setSelected(template);
    setSubject(row.subject);
    setBody(row.body);
    setRendered(null);
    setCompareId(null);
  }

  const testMutation = useMutation({
    mutationFn: () =>
      testSend({
        data: {
          template: selected!,
          subject,
          body,
          to: testTo.trim(),
          label: testLabel.trim(),
        },
      }),
    onSuccess: (result) => {
      const failures = result.results.filter((row) => !row.sent);
      if (failures.length === 0) {
        toast.success(`Test sent to ${result.sentCount} address${result.sentCount === 1 ? "" : "es"}`, {
          description: "Queued notices were not touched.",
        });
      } else {
        toast.warning(`${result.sentCount} of ${result.total} delivered`, {
          description: failures
            .map((row) => `${row.recipient}: ${row.reason ?? "not delivered"}`)
            .join(" · "),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["notice-test-runs", selected] });
    },
    onError: (e: Error) => toast.error("Test send failed", { description: e.message }),
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) => restore({ data: { versionId } }),
    onSuccess: async () => {
      toast.success("Restored that version — it applies to the next notice sent");
      const fresh = await queryClient.fetchQuery({
        queryKey: ["notice-templates"],
        queryFn: () => load(),
      });
      const row = fresh?.find((item) => item.template === selected);
      if (row) {
        setSubject(row.subject);
        setBody(row.body);
      }
      void queryClient.invalidateQueries({ queryKey: ["notice-template-versions", selected] });
      setCompareId(null);
    },
    onError: (e: Error) => toast.error("Could not restore", { description: e.message }),
  });

  const compared = versions?.find((version) => version.id === compareId) ?? null;
  const against =
    compareAgainst === "current"
      ? { label: "the wording on screen", subject, body }
      : (() => {
          const version = versions?.find((row) => row.id === compareAgainst);
          return version
            ? {
                label: new Date(version.createdAt).toLocaleString(),
                subject: version.subject,
                body: version.body,
              }
            : { label: "the wording on screen", subject, body };
        })();
  const subjectDiff = compared ? diffLines(compared.subject, against.subject) : [];
  const bodyDiff = compared ? diffLines(compared.body, against.body) : [];
  const changeCount = compared ? diffSummary([...subjectDiff, ...bodyDiff]) : null;
  const subjectRows = compared ? alignedDiff(compared.subject, against.subject) : [];
  const bodyRows = compared ? alignedDiff(compared.body, against.body) : [];

  const saveMutation = useMutation({
    mutationFn: () =>
      save({ data: { template: selected!, subject, body, note: versionNote.trim() } }),
    onSuccess: () => {
      toast.success("Wording saved — it applies to the next notice sent");
      setVersionNote("");
      void queryClient.invalidateQueries({ queryKey: ["notice-template-versions", selected] });
      void queryClient.invalidateQueries({ queryKey: ["notice-templates"] });
    },
    onError: (e: Error) => toast.error("Could not save", { description: e.message }),
  });

  const resetMutation = useMutation({
    mutationFn: () => reset({ data: { template: selected! } }),
    onSuccess: async () => {
      toast.success("Reverted to the built-in wording");
      const fresh = await queryClient.fetchQuery({
        queryKey: ["notice-templates"],
        queryFn: () => load(),
      });
      const row = fresh?.find((item) => item.template === selected);
      if (row) {
        setSubject(row.subject);
        setBody(row.body);
      }
      setRendered(null);
    },
    onError: (e: Error) => toast.error("Could not reset", { description: e.message }),
  });

  const previewMutation = useMutation({
    mutationFn: () => preview({ data: { subject, body } }),
    onSuccess: (result) => setRendered(result),
    onError: (e: Error) => toast.error("Preview failed", { description: e.message }),
  });

  const previewForRecipient = useServerFn(previewVariablesForRecipient);
  const recipientPreviewMutation = useMutation({
    mutationFn: () =>
      previewForRecipient({ data: { recipient: previewRecipient, subject, body } }),
    onError: (e: Error) =>
      toast.error("Could not build that preview", { description: e.message }),
  });
  const recipientPreview = recipientPreviewMutation.data;

  const previewForRecipients = useServerFn(previewVariablesForRecipients);
  const multiPreviewMutation = useMutation({
    mutationFn: () =>
      previewForRecipients({ data: { recipients: multiRecipients, subject, body } }),
    onError: (e: Error) =>
      toast.error("Could not build those previews", { description: e.message }),
  });
  const multiPreview = multiPreviewMutation.data;

  // Soft advice: nothing here blocks saving, it only flags wording that has
  // drifted from what this notice normally tells the member.
  const warnings = current
    ? analyzeTemplateVariables(subject, body, current.defaultSubject, current.defaultBody)
    : [];

  const dirty = Boolean(current) && (subject !== current!.subject || body !== current!.body);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Email templates"
        description="Approval, rejection, appeal and support wording. Edits take effect on the next notice — no redeploy, and previously sent notices keep the words the member actually received."
      />

      {error ? (
        <p className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          {error.message}
        </p>
      ) : isLoading ? (
        <Skeleton className="mt-8 h-96 w-full rounded-2xl" />
      ) : (
        <div className="mt-8 grid gap-6 lg:grid-cols-[16rem_1fr]">
          <nav aria-label="Notice templates" className="space-y-1">
            {(data ?? []).map((row) => (
              <button
                key={row.template}
                type="button"
                onClick={() => choose(row.template)}
                className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm transition ${
                  row.template === selected
                    ? "border-primary/60 bg-primary/10 text-foreground"
                    : "border-border/60 bg-surface/40 text-muted-foreground hover:text-foreground"
                }`}
              >
                <span>{row.template}</span>
                {row.customised ? (
                  <Badge variant="outline" className="text-[10px]">
                    edited
                  </Badge>
                ) : null}
              </button>
            ))}
          </nav>

          {current ? (
            <div className="space-y-5 rounded-2xl border border-border/60 bg-surface/40 p-5">
              <div className="relative space-y-2">
                <Label htmlFor="template-subject">Subject</Label>
                <Input
                  id="template-subject"
                  ref={subjectRef}
                  value={subject}
                  onChange={(event) => {
                    setSubject(event.target.value);
                    trackCaret("subject", event.target);
                  }}
                  onKeyUp={(event) => trackCaret("subject", event.currentTarget)}
                  onKeyDown={(event) => handleKeyDown("subject", event)}
                  onBlur={() => setTimeout(() => setAutocomplete(null), 120)}
                  autoComplete="off"
                />
                {autocomplete?.field === "subject" ? (
                  <SuggestionList
                    suggestions={suggestions}
                    activeIndex={autocomplete.index}
                    onPick={(name) => applyVariable("subject", name)}
                  />
                ) : null}
              </div>
              <div className="relative space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="template-body">Body</Label>
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button size="sm" variant="ghost">
                        Variable help
                      </Button>
                    </SheetTrigger>
                    <SheetContent className="w-full overflow-y-auto sm:max-w-md">
                      <SheetHeader>
                        <SheetTitle>Variables you can use</SheetTitle>
                        <SheetDescription>
                          Type <span className="font-mono">{"{{"}</span> in the subject or body to
                          pick one, or insert it here. Anything not on this list is sent to the
                          member exactly as written.
                        </SheetDescription>
                      </SheetHeader>
                      <ul className="mt-6 space-y-4">
                        {PLACEHOLDER_DOCS.map((doc) => (
                          <li key={doc.name} className="rounded-xl border border-border/60 p-4">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-sm text-foreground">{doc.token}</span>
                              <Button
                                size="sm"
                                variant="soft"
                                onClick={() => applyVariable("body", doc.name)}
                              >
                                Insert
                              </Button>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">{doc.description}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Becomes: “{doc.example}” · {doc.usedIn}
                            </p>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 text-xs text-muted-foreground">
                        Variables are case sensitive and take no spaces inside the braces.
                        Quibblies adds the unsubscribe footer to every email automatically.
                      </p>
                    </SheetContent>
                  </Sheet>
                </div>
                <Textarea
                  id="template-body"
                  ref={bodyRef}
                  rows={14}
                  value={body}
                  onChange={(event) => {
                    setBody(event.target.value);
                    trackCaret("body", event.target);
                  }}
                  onKeyUp={(event) => trackCaret("body", event.currentTarget)}
                  onKeyDown={(event) => handleKeyDown("body", event)}
                  onBlur={() => setTimeout(() => setAutocomplete(null), 120)}
                />
                {autocomplete?.field === "body" ? (
                  <SuggestionList
                    suggestions={suggestions}
                    activeIndex={autocomplete.index}
                    onPick={(name) => applyVariable("body", name)}
                  />
                ) : null}
                <p className="text-xs text-muted-foreground">
                  Placeholders: {PLACEHOLDERS.join(" · ")} — anything else is sent literally.
                  Quibblies adds the unsubscribe footer to every email automatically.
                </p>
              </div>

              {problems.length > 0 ? (
                <div
                  role="alert"
                  className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm"
                >
                  <p className="font-medium text-foreground">
                    {problems.length} placeholder problem{problems.length === 1 ? "" : "s"} — fix
                    these before saving or testing.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {problems.map((problem, index) => (
                      <li key={`${problem.token}-${index}`}>
                        <span className="font-mono text-foreground">{problem.token}</span> in the{" "}
                        {problem.field} — {problem.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {warnings.length > 0 ? (
                <div className="rounded-xl border border-border/60 bg-surface/40 p-4 text-sm">
                  <p className="font-medium text-foreground">Things worth checking</p>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {warnings.map((warning, index) => (
                      <li key={`${warning.token}-${index}`}>
                        <span className="font-mono text-foreground">{warning.token}</span> —{" "}
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="space-y-1">
                <Label htmlFor="version-note">What changed (optional)</Label>
                <Input
                  id="version-note"
                  placeholder="e.g. Softened the rejection wording and added the appeal link"
                  value={versionNote}
                  onChange={(event) => setVersionNote(event.target.value)}
                  maxLength={300}
                />
                <p className="text-xs text-muted-foreground">
                  Saved alongside this version so the history explains itself later.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => saveMutation.mutate()}
                  disabled={!dirty || saveMutation.isPending || problems.length > 0}
                >
                  Save wording
                </Button>
                <Button
                  variant="soft"
                  onClick={() => previewMutation.mutate()}
                  disabled={previewMutation.isPending}
                >
                  Preview with sample data
                </Button>
                {current.customised ? (
                  <Button
                    variant="ghost"
                    onClick={() => resetMutation.mutate()}
                    disabled={resetMutation.isPending}
                  >
                    Revert to default
                  </Button>
                ) : null}
              </div>

              {rendered ? (
                <div className="rounded-xl border border-border/60 bg-background p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Preview</p>
                  <p className="mt-1 font-display text-lg text-foreground">{rendered.subject}</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
                    {rendered.body}
                  </p>
                </div>
              ) : null}

              <div className="rounded-xl border border-border/60 bg-background/60 p-4">
                <h3 className="font-display text-base text-foreground">
                  Preview for a real recipient
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Shows what each variable becomes for one member before you send anything. Nothing
                  is sent and no queued notice is touched.
                </p>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <div className="min-w-[16rem] flex-1 space-y-1">
                    <Label htmlFor="preview-recipient">Their email address</Label>
                    <Input
                      id="preview-recipient"
                      type="email"
                      placeholder="member@example.com"
                      value={previewRecipient}
                      onChange={(event) => setPreviewRecipient(event.target.value)}
                    />
                  </div>
                  <Button
                    variant="soft"
                    onClick={() => recipientPreviewMutation.mutate()}
                    disabled={!previewRecipient.trim() || recipientPreviewMutation.isPending}
                  >
                    {recipientPreviewMutation.isPending ? "Resolving…" : "Resolve variables"}
                  </Button>
                </div>

                {recipientPreview ? (
                  <div className="mt-4 space-y-4">
                    {recipientPreview.known ? null : (
                      <p className="rounded-lg border border-border/60 bg-surface/40 p-3 text-xs text-muted-foreground">
                        No member on file for that address, so every variable below shows the
                        fallback wording that would be used.
                      </p>
                    )}
                    <div className="overflow-x-auto rounded-lg border border-border/60">
                      <table className="w-full min-w-[520px] text-left text-sm">
                        <caption className="sr-only">
                          How each variable resolves for {recipientPreview.recipient}
                        </caption>
                        <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2">Variable</th>
                            <th className="px-3 py-2">Becomes</th>
                            <th className="px-3 py-2">Where it comes from</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recipientPreview.variables.map((variable) => (
                            <tr key={variable.name} className="border-t border-border/50 align-top">
                              <td className="px-3 py-2 font-mono text-xs text-foreground">
                                {variable.token}
                              </td>
                              <td className="px-3 py-2 text-foreground">
                                {variable.value}
                                {variable.resolved ? null : (
                                  <Badge variant="outline" className="ml-2 text-[10px]">
                                    fallback
                                  </Badge>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">
                                {variable.source}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-background p-4">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        As they would read it
                      </p>
                      <p className="mt-1 font-display text-lg text-foreground">
                        {recipientPreview.subject}
                      </p>
                      <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
                        {recipientPreview.body}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-border/60 bg-background/60 p-4">
                <h3 className="font-display text-base text-foreground">
                  Preview for several addresses at once
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Resolves the variables and the finished subject and body for up to 10 addresses
                  in one run. Nothing is sent.
                </p>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <div className="min-w-[18rem] flex-1 space-y-1">
                    <Label htmlFor="multi-preview">Addresses</Label>
                    <Input
                      id="multi-preview"
                      placeholder="one@example.com, two@example.com"
                      value={multiRecipients}
                      onChange={(event) => setMultiRecipients(event.target.value)}
                    />
                  </div>
                  <Button
                    variant="soft"
                    onClick={() => multiPreviewMutation.mutate()}
                    disabled={!multiRecipients.trim() || multiPreviewMutation.isPending}
                  >
                    {multiPreviewMutation.isPending ? "Resolving…" : "Preview all"}
                  </Button>
                </div>

                {multiPreview ? (
                  <ul className="mt-4 space-y-4">
                    {multiPreview.previews.map((row) => (
                      <li key={row.recipient} className="rounded-lg border border-border/60 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-foreground">{row.recipient}</span>
                          {row.known ? null : (
                            <Badge variant="outline" className="text-[10px]">
                              no member on file
                            </Badge>
                          )}
                        </div>
                        <p className="mt-2 font-display text-base text-foreground">{row.subject}</p>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                          {row.body}
                        </p>
                        <ul className="mt-3 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                          {row.variables.map((variable) => (
                            <li key={variable.name}>
                              <span className="font-mono text-foreground">{variable.token}</span> →{" "}
                              {variable.value}
                              {variable.resolved ? "" : " (fallback)"}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="rounded-xl border border-border/60 bg-background/60 p-4">
                <h3 className="font-display text-base text-foreground">Send a test</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Sends exactly what is on screen (unsaved edits included) with sample values, to
                  up to 10 addresses. Each run is saved below against the current wording version —
                  no member receives it and no queued notice changes.
                </p>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <div className="min-w-[16rem] flex-1 space-y-1">
                    <Label htmlFor="test-to">Send to</Label>
                    <Input
                      id="test-to"
                      placeholder="Your own address (default) — or several, comma separated"
                      value={testTo}
                      onChange={(event) => setTestTo(event.target.value)}
                    />
                  </div>
                  <div className="min-w-[12rem] space-y-1">
                    <Label htmlFor="test-label">Label this run</Label>
                    <Input
                      id="test-label"
                      placeholder="e.g. Softer rejection wording"
                      value={testLabel}
                      onChange={(event) => setTestLabel(event.target.value)}
                    />
                  </div>
                  <Button
                    variant="soft"
                    onClick={() => testMutation.mutate()}
                    disabled={testMutation.isPending || problems.length > 0}
                  >
                    {testMutation.isPending ? "Sending…" : "Send test email"}
                  </Button>
                </div>

                {(testRuns ?? []).length > 0 ? (
                  <ul className="mt-4 space-y-2">
                    {(testRuns ?? []).map((run) => (
                      <li
                        key={run.id}
                        className="rounded-lg border border-border/50 px-3 py-2 text-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-foreground">{run.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(run.createdAt).toLocaleString()}
                            {run.templateVersionId ? (
                              <Badge variant="outline" className="ml-2 text-[10px]">
                                version linked
                              </Badge>
                            ) : null}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {run.sentCount} delivered
                          {run.failedCount ? `, ${run.failedCount} not delivered` : ""} ·{" "}
                          {run.recipients.join(", ")}
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="mt-1 px-0"
                          onClick={() => runCsvMutation.mutate(run.id)}
                          disabled={runCsvMutation.isPending}
                        >
                          Download CSV
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>


              <div className="rounded-xl border border-border/60 bg-background/60 p-4">
                <h3 className="font-display text-base text-foreground">Version history</h3>
                {(versions ?? []).length === 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    No saved versions yet — the first edit you save appears here.
                  </p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {(versions ?? []).map((version) => (
                      <li
                        key={version.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/50 px-3 py-2 text-sm"
                      >
                        <span className="text-muted-foreground">
                          {new Date(version.createdAt).toLocaleString()}
                          {version.changedByName ? ` · ${version.changedByName}` : ""}
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            {version.changeType.replaceAll("_", " ")}
                          </Badge>
                          {version.note ? (
                            <span className="mt-1 block text-xs text-foreground">
                              “{version.note}”
                            </span>
                          ) : null}
                        </span>
                        <span className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setCompareId(compareId === version.id ? null : version.id)
                            }
                          >
                            {compareId === version.id ? "Hide diff" : "Compare"}
                          </Button>
                          <Button
                            size="sm"
                            variant="soft"
                            onClick={() => restoreMutation.mutate(version.id)}
                            disabled={restoreMutation.isPending}
                          >
                            Restore
                          </Button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {compared ? (
                  <div className="mt-4 rounded-lg border border-border/60 bg-background p-3">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="compare-against">Compare against</Label>
                        <select
                          id="compare-against"
                          className="h-9 rounded-md border border-border/60 bg-background px-2 text-sm text-foreground"
                          value={compareAgainst}
                          onChange={(event) => setCompareAgainst(event.target.value)}
                        >
                          <option value="current">The wording on screen</option>
                          {(versions ?? [])
                            .filter((version) => version.id !== compared.id)
                            .map((version) => (
                              <option key={version.id} value={version.id}>
                                {new Date(version.createdAt).toLocaleString()} (
                                {version.changeType.replaceAll("_", " ")})
                              </option>
                            ))}
                        </select>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setSideBySide((v) => !v)}>
                        {sideBySide ? "Show as one column" : "Show side by side"}
                      </Button>
                    </div>

                    <p className="mt-3 text-xs text-muted-foreground">
                      {new Date(compared.createdAt).toLocaleString()} compared with {against.label} —{" "}
                      {changeCount?.added ?? 0} added, {changeCount?.removed ?? 0} removed.
                    </p>

                    {sideBySide ? (
                      <div className="mt-3 space-y-4">
                        {[
                          { heading: "Subject", rows: subjectRows },
                          { heading: "Body", rows: bodyRows },
                        ].map((part) => (
                          <div key={part.heading}>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              {part.heading}
                            </p>
                            <div className="mt-1 overflow-x-auto rounded-md border border-border/60">
                              <table className="w-full min-w-[640px] table-fixed text-left font-mono text-xs">
                                <caption className="sr-only">
                                  {part.heading} differences, before on the left and after on the
                                  right
                                </caption>
                                <thead className="bg-surface/60 text-[10px] uppercase tracking-wide text-muted-foreground">
                                  <tr>
                                    <th className="w-1/2 px-3 py-2">
                                      Before · {new Date(compared.createdAt).toLocaleDateString()}
                                    </th>
                                    <th className="w-1/2 px-3 py-2">After · {against.label}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {part.rows.map((row, index) => (
                                    <tr key={index} className="border-t border-border/40 align-top">
                                      <td
                                        className={`whitespace-pre-wrap break-words px-3 py-1 ${
                                          row.kind === "same"
                                            ? "text-muted-foreground"
                                            : row.left === null
                                              ? "bg-muted/30"
                                              : "bg-destructive/10 text-muted-foreground"
                                        }`}
                                      >
                                        {row.left ?? ""}
                                      </td>
                                      <td
                                        className={`whitespace-pre-wrap break-words px-3 py-1 ${
                                          row.kind === "same"
                                            ? "text-muted-foreground"
                                            : row.right === null
                                              ? "bg-muted/30"
                                              : "bg-primary/10 text-foreground"
                                        }`}
                                      >
                                        {row.right ?? ""}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap font-mono text-xs">
                        {[...subjectDiff, { kind: "same" as const, text: "" }, ...bodyDiff].map(
                          (line, index) => (
                            <div
                              key={index}
                              className={
                                line.kind === "added"
                                  ? "bg-primary/10 text-foreground"
                                  : line.kind === "removed"
                                    ? "bg-destructive/10 text-muted-foreground line-through"
                                    : "text-muted-foreground"
                              }
                            >
                              {line.kind === "added" ? "+ " : line.kind === "removed" ? "- " : "  "}
                              {line.text}
                            </div>
                          ),
                        )}
                      </pre>
                    )}
                  </div>
                ) : null}
              </div>

              {current.updatedAt ? (
                <p className="text-xs text-muted-foreground">
                  Last edited {new Date(current.updatedAt).toLocaleString()}.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Variable suggestions shown while a placeholder is being typed. */
function SuggestionList({
  suggestions,
  activeIndex,
  onPick,
}: {
  suggestions: typeof PLACEHOLDER_DOCS;
  activeIndex: number;
  onPick: (name: string) => void;
}) {
  if (suggestions.length === 0) return null;
  return (
    <ul
      role="listbox"
      aria-label="Variable suggestions"
      className="absolute z-20 mt-1 w-full max-w-md overflow-hidden rounded-xl border border-border/60 bg-background shadow-lg"
    >
      {suggestions.map((doc, index) => (
        <li key={doc.name}>
          <button
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onPick(doc.name)}
            className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm ${
              index === activeIndex ? "bg-primary/10 text-foreground" : "text-muted-foreground"
            }`}
          >
            <span className="font-mono text-foreground">{doc.token}</span>
            <span className="text-xs">{doc.description}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
